import { VisitorData, VisitorVariationUpdateParam } from '@src/container/handleIframeMessage/types';
import {
  FsDataState,
  FsReducerActionUpdateVisitorVariation,
  FsCampaign,
  FsSearchCampaigns,
  WebSDKCampaignStatus,
  CampaignDisplayStatus,
  FsVariationAllocationAction,
  FsVariationToForce,
  VariationGroupDTO,
  TargetingGroups,
} from '../../type';
import { saveVariationsForcedAllocation, saveVariationsForcedUnallocation } from '../../utils';
import { matchesTargetingCriteria } from '../helpers';

/**
 * Evaluates if all targetings in a group match the visitor data
 * Updates hasMatched property for each targeting as a side effect
 */
function evaluateTargetingGroup(targetingGroup: TargetingGroups, visitorData?: VisitorData): boolean {
  if (targetingGroup.targetings.length === 0) {
    return false;
  }

  let allMatched = true;

  for (const targeting of targetingGroup.targetings) {
    targeting.hasMatched = matchesTargetingCriteria(targeting, visitorData);

    if (!targeting.hasMatched) {
      allMatched = false;
    }
  }

  return allMatched;
}

/**
 * Evaluates if any variation group in a campaign matches the visitor data
 */
function evaluateCampaignTargeting(variationGroups: VariationGroupDTO[], visitorData?: VisitorData): boolean {
  for (const vg of variationGroups) {
    for (const tg of vg.targeting.targetingGroups) {
      if (evaluateTargetingGroup(tg, visitorData)) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Generic campaign search function
 */
function searchCampaigns(campaigns: FsCampaign[], searchValue: string): FsCampaign[] {
  if (!searchValue) {
    return campaigns;
  }

  const upperSearchValue = searchValue.toUpperCase();
  return campaigns.filter(
    (item) => item.id.toUpperCase().startsWith(upperSearchValue) || item.name.toUpperCase().startsWith(upperSearchValue)
  );
}

/**
 * Sorts campaigns by status (ACCEPTED first) then by name in ascending order
 */
function sortCampaigns(campaigns: FsCampaign[]): FsCampaign[] {
  return campaigns.sort((a, b) => {
    // First, sort by status (ACCEPTED first)
    if (a.status === WebSDKCampaignStatus.ACCEPTED && b.status !== WebSDKCampaignStatus.ACCEPTED) {
      return -1;
    }
    if (a.status !== WebSDKCampaignStatus.ACCEPTED && b.status === WebSDKCampaignStatus.ACCEPTED) {
      return 1;
    }

    // Then sort by name in ascending order
    return a.name.localeCompare(b.name);
  });
}

/**
 * Separates campaigns into allocated and non-allocated categories based on visitor variations
 */
export function splitCampaignsByAllocation(state: FsDataState, action: FsReducerActionUpdateVisitorVariation) {
  const allAcceptedCampaigns: FsCampaign[] = [];
  const allRejectedCampaigns: FsCampaign[] = [];

  const campaigns = state.bucketingFile?.campaigns ?? [];
  const visitorData = action.visitorData;

  campaigns.forEach((campaign) => {
    const isAllocated = !!action.value[campaign.id];
    const isForcedAllocation = !!state.variationsForcedAllocation[campaign.id];
    const isForcedUnallocation = !!state.variationsForcedUnallocation[campaign.id];

    let updatedCampaign = { ...campaign };

    updatedCampaign.hasTargetingMatched = evaluateCampaignTargeting(updatedCampaign.variationGroups, visitorData);

    if (isAllocated) {
      updatedCampaign.status = WebSDKCampaignStatus.ACCEPTED;

      if (isForcedAllocation) {
        updatedCampaign.displayStatus = CampaignDisplayStatus.RESET;
        updatedCampaign.status = WebSDKCampaignStatus.REJECTED;
      } else if (isForcedUnallocation) {
        updatedCampaign.displayStatus = CampaignDisplayStatus.RESET;
      } else {
        updatedCampaign.displayStatus = CampaignDisplayStatus.SHOWN;
      }
      allAcceptedCampaigns.push(updatedCampaign);
    } else {
      updatedCampaign.status = WebSDKCampaignStatus.REJECTED;

      if (isForcedUnallocation) {
        updatedCampaign.displayStatus = CampaignDisplayStatus.RESET;
        updatedCampaign.status = WebSDKCampaignStatus.ACCEPTED;
      } else if (isForcedAllocation) {
        updatedCampaign.displayStatus = CampaignDisplayStatus.RESET;
      } else {
        updatedCampaign.displayStatus = CampaignDisplayStatus.HIDDEN;
      }
      allRejectedCampaigns.push(updatedCampaign);
    }
  });

  return {
    allAcceptedCampaigns,
    allRejectedCampaigns,
  };
}

export function renderCampaignsAfterDelay(state: FsDataState): FsDataState {
  const displayedExposedCampaigns: FsCampaign[] = [];
  const displayedRejectedCampaigns: FsCampaign[] = [];
  const displayedUnexposedCampaigns: FsCampaign[] = [];

  if (state.hasReceivedExposureEvent) {
    return state;
  }

  state.allAcceptedCampaigns.forEach((campaign) => {
    if (state.variationsForcedUnallocation[campaign.id]) {
      displayedRejectedCampaigns.push(campaign);
    } else {
      displayedUnexposedCampaigns.push(campaign);
    }
  });

  state.allRejectedCampaigns.forEach((campaign) => {
    if (state.variationsForcedAllocation[campaign.id]) {
      displayedUnexposedCampaigns.push(campaign);
    } else {
      displayedRejectedCampaigns.push(campaign);
    }
  });

  return {
    ...state,
    hasReceivedExposureEvent: true,
    displayedExposedCampaigns: sortCampaigns(displayedExposedCampaigns),
    displayedUnexposedCampaigns: sortCampaigns(displayedUnexposedCampaigns),
    displayedRejectedCampaigns: sortCampaigns(displayedRejectedCampaigns),
  };
}

/**
 * Filters campaigns by their exposure status
 */
export function filterVisitorExposedCampaigns(state: FsDataState, action: FsReducerActionUpdateVisitorVariation) {
  const displayedExposedCampaigns: FsCampaign[] = [];
  const displayedRejectedCampaigns: FsCampaign[] = [];
  const displayedUnexposedCampaigns: FsCampaign[] = [];

  const currentExposedIds = new Set(state.allExposedCampaigns.map((campaign) => campaign.id));
  const isNewNavigation = action.param === VisitorVariationUpdateParam.NewNavigation;

  state.allAcceptedCampaigns.forEach((campaign) => {
    if (state.variationsForcedUnallocation[campaign.id]) {
      displayedRejectedCampaigns.push(campaign);
    } else if (action.value[campaign.id] || (!isNewNavigation && currentExposedIds.has(campaign.id))) {
      displayedExposedCampaigns.push(campaign);
    } else {
      displayedUnexposedCampaigns.push(campaign);
    }
  });

  state.allRejectedCampaigns.forEach((campaign) => {
    if (action.value[campaign.id]) {
      displayedExposedCampaigns.push(campaign);
    } else if (state.variationsForcedAllocation[campaign.id]) {
      displayedUnexposedCampaigns.push(campaign);
    } else {
      displayedRejectedCampaigns.push(campaign);
    }
  });

  return {
    displayedExposedCampaigns: sortCampaigns(displayedExposedCampaigns),
    displayedUnexposedCampaigns: sortCampaigns(displayedUnexposedCampaigns),
    displayedRejectedCampaigns: sortCampaigns(displayedRejectedCampaigns),
  };
}

/**
 * Searches exposed campaigns by ID or name
 */
export function searchExposedCampaigns(state: FsDataState, action: FsSearchCampaigns): FsCampaign[] {
  return searchCampaigns(state.allExposedCampaigns, action.value);
}

/**
 * Searches unexposed campaigns by ID or name
 */
export function searchUnExposedCampaigns(state: FsDataState, action: FsSearchCampaigns): FsCampaign[] {
  const currentExposedIds = new Set(state.allExposedCampaigns.map((campaign) => campaign.id));
  const unExposedCampaigns = state.allAcceptedCampaigns.filter((campaign) => !currentExposedIds.has(campaign.id));
  return searchCampaigns(unExposedCampaigns, action.value);
}

/**
 * Searches non-allocated campaigns by ID or name
 */
export function searchNonAllocatedCampaigns(state: FsDataState, action: FsSearchCampaigns): FsCampaign[] {
  return searchCampaigns(state.allRejectedCampaigns, action.value);
}

/**
 * Forces a variation allocation for a campaign
 */
export function forceVariationAllocation(state: FsDataState, action: FsVariationAllocationAction): FsDataState {
  const data = action.value;

  const variationsForcedAllocation: Record<string, FsVariationToForce> = {
    ...state.variationsForcedAllocation,
    [data.campaignId]: data,
  };

  saveVariationsForcedAllocation(variationsForcedAllocation);

  return {
    ...state,
    variationsForcedAllocation,
    shouldSendForcedAllocation: true,
    hasReceivedExposureEvent: false,
  };
}

/**
 * Forces a variation unallocation for a campaign
 */
export function forceVariationUnallocation(state: FsDataState, action: FsVariationAllocationAction): FsDataState {
  const { campaignId } = action.value;

  const variationsForcedUnallocation: Record<string, FsVariationToForce> = {
    ...state.variationsForcedUnallocation,
    [campaignId]: action.value,
  };

  saveVariationsForcedUnallocation(variationsForcedUnallocation);

  return {
    ...state,
    variationsForcedUnallocation,
    shouldSendForcedUnallocation: true,
    hasReceivedExposureEvent: false,
  };
}

/**
 * Removes a forced variation unallocation
 */
export function removeForcedVariationUnallocation(
  state: FsDataState,
  action: FsVariationAllocationAction
): FsDataState {
  const { campaignId } = action.value;

  const { [campaignId]: _, ...updatedVariationsForcedUnallocation } = state.variationsForcedUnallocation;

  saveVariationsForcedUnallocation(updatedVariationsForcedUnallocation);

  return {
    ...state,
    variationsForcedUnallocation: updatedVariationsForcedUnallocation,
    shouldSendForcedUnallocation: true,
    hasReceivedExposureEvent: false,
  };
}

/**
 * Removes a forced variation allocation
 */
export function removeForcedVariationAllocation(state: FsDataState, action: FsVariationAllocationAction): FsDataState {
  const { campaignId } = action.value;

  const { [campaignId]: _, ...updatedVariationsForcedAllocation } = state.variationsForcedAllocation;

  saveVariationsForcedAllocation(updatedVariationsForcedAllocation);

  return {
    ...state,
    variationsForcedAllocation: updatedVariationsForcedAllocation,
    shouldSendForcedAllocation: true,
    hasReceivedExposureEvent: false,
  };
}
